import { motion } from "framer-motion";
import { ReactNode, useEffect, useState } from "react";

interface NeonTextProps {
  children: ReactNode;
  className?: string;
  color?: "pink" | "red" | "purple" | "roseGold";
  animate?: boolean;
  typewriter?: boolean;
  flickering?: boolean;
}

const NeonText = ({ 
  children, 
  className = "", 
  color = "pink",
  animate = true,
  typewriter = false,
  flickering = false,
}: NeonTextProps) => {
  const [displayedText, setDisplayedText] = useState("");
  const [showCursor, setShowCursor] = useState(true);
  
  const text = typeof children === "string" ? children : "";

  const colorStyles = {
    pink: {
      textColor: "text-pink-400",
      glowColor: "rgba(236, 72, 153, 0.8)",
      glowColorStrong: "rgba(236, 72, 153, 1)",
    },
    red: {
      textColor: "text-red-400",
      glowColor: "rgba(239, 68, 68, 0.8)",
      glowColorStrong: "rgba(239, 68, 68, 1)",
    },
    purple: {
      textColor: "text-purple-400",
      glowColor: "rgba(168, 85, 247, 0.8)",
      glowColorStrong: "rgba(168, 85, 247, 1)",
    },
    roseGold: {
      textColor: "text-amber-400",
      glowColor: "rgba(251, 191, 36, 0.8)",
      glowColorStrong: "rgba(251, 191, 36, 1)",
    },
  };

  const styles = colorStyles[color];

  // Typewriter effect
  useEffect(() => {
    if (!typewriter || !text) {
      setDisplayedText(text);
      return;
    }

    setDisplayedText("");
    let index = 0;
    const interval = setInterval(() => {
      if (index < text.length) {
        setDisplayedText(text.slice(0, index + 1));
        index++;
      } else {
        clearInterval(interval);
      }
    }, 80);

    return () => clearInterval(interval);
  }, [text, typewriter]);

  // Cursor blink
  useEffect(() => {
    if (!typewriter) return;
    const interval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 530);
    return () => clearInterval(interval);
  }, [typewriter]);

  const glowAnimation = animate ? {
    textShadow: [
      `0 0 10px ${styles.glowColor}, 0 0 20px ${styles.glowColor}, 0 0 30px ${styles.glowColor}`,
      `0 0 15px ${styles.glowColorStrong}, 0 0 30px ${styles.glowColorStrong}, 0 0 45px ${styles.glowColor}`,
      `0 0 10px ${styles.glowColor}, 0 0 20px ${styles.glowColor}, 0 0 30px ${styles.glowColor}`,
    ],
  } : {};

  const flickerAnimation = flickering ? {
    opacity: [1, 0.85, 1, 0.9, 1, 0.95, 1, 0.88, 1],
  } : {};

  return (
    <motion.span
      className={`${styles.textColor} ${className}`}
      style={{
        textShadow: `0 0 10px ${styles.glowColor}, 0 0 20px ${styles.glowColor}, 0 0 30px ${styles.glowColor}`,
      }}
      animate={{
        ...glowAnimation,
        ...flickerAnimation,
      }}
      transition={{
        duration: flickering ? 0.5 : 2,
        repeat: Infinity,
        ease: flickering ? "linear" : "easeInOut",
      }}
    >
      {typewriter ? (
        <>
          {displayedText}
          {showCursor && displayedText.length < text.length && (
            <span className="animate-pulse">|</span>
          )}
        </>
      ) : (
        children
      )}
    </motion.span>
  );
};

export default NeonText;
